import React from 'react'
import { Link } from '@inertiajs/react'
import { AlertCircle, AlertTriangle, Info, ArrowRight, Coffee } from 'lucide-react'

interface ActionItem {
  severity: 'error' | 'warning' | 'info'
  title: string
  message: string
  propertyId: number
  propertyName: string
  /** The canonical path of the hotel this item is about — backend-served, never rebuilt from id. */
  propertyBasePath: string
  age: string | null
}

interface ActionCenterProps {
  items: ActionItem[]
}

const SEVERITY_ORDER: Record<ActionItem['severity'], number> = {
  error: 0,
  warning: 1,
  info: 2,
}

function severityChrome(severity: ActionItem['severity']) {
  if (severity === 'error') {
    return {
      Icon: AlertCircle,
      stripe: 'bg-rose-500',
      iconCls: 'text-rose-500',
      pill: 'bg-rose-50 text-rose-700 ring-rose-200',
      label: 'Error',
    }
  }
  if (severity === 'warning') {
    return {
      Icon: AlertTriangle,
      stripe: 'bg-amber-500',
      iconCls: 'text-amber-500',
      pill: 'bg-amber-50 text-amber-800 ring-amber-200',
      label: 'Warning',
    }
  }
  return {
    Icon: Info,
    stripe: 'bg-slate-400',
    iconCls: 'text-slate-400',
    pill: 'bg-slate-100 text-slate-700 ring-slate-200',
    label: 'Note',
  }
}

export default function ActionCenter({ items }: ActionCenterProps) {
  const sorted = [...items].sort((a, b) => SEVERITY_ORDER[a.severity] - SEVERITY_ORDER[b.severity])

  return (
    <div>
      <div className="mb-5">
        <div className="text-[10px] font-semibold uppercase tracking-[0.22em] text-primary">
          On the desk
        </div>
        <h2 className="mt-1.5 text-xl font-semibold tracking-tight text-foreground sm:text-2xl">
          {sorted.length === 0 ? 'A quiet morning' : 'Needs your attention'}
        </h2>
      </div>

      {sorted.length === 0 ? (
        <EmptyState />
      ) : (
        <div className="overflow-hidden rounded-xl border border-border/60 bg-white shadow-sm">
          <ul className="divide-y divide-border/40">
            {sorted.map((item, idx) => {
              const chrome = severityChrome(item.severity)
              const Icon = chrome.Icon
              return (
                <li key={idx} className="group relative">
                  <Link
                    href={item.propertyBasePath}
                    className="flex items-stretch gap-0 transition-colors hover:bg-muted/30"
                  >
                    <span className={`w-1 shrink-0 ${chrome.stripe}`} aria-hidden />
                    <div className="flex flex-1 items-start gap-4 px-5 py-4">
                      <Icon className={`mt-0.5 h-4 w-4 shrink-0 ${chrome.iconCls}`} />
                      <div className="flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span
                            className={`inline-flex items-center rounded-md px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider ring-1 ring-inset ${chrome.pill}`}
                          >
                            {chrome.label}
                          </span>
                          <span className="text-sm font-semibold text-foreground">{item.title}</span>
                          <span className="text-[11px] text-muted-foreground">·</span>
                          <span className="text-[11px] font-medium text-foreground">{item.propertyName}</span>
                          {item.age && (
                            <>
                              <span className="text-[11px] text-muted-foreground">·</span>
                              <span className="text-[11px] text-muted-foreground tabular-nums">{item.age}</span>
                            </>
                          )}
                        </div>
                        <p className="mt-1 text-sm text-muted-foreground">{item.message}</p>
                      </div>
                      <ArrowRight className="mt-1 h-4 w-4 shrink-0 text-muted-foreground/40 transition-all group-hover:translate-x-0.5 group-hover:text-foreground" />
                    </div>
                  </Link>
                </li>
              )
            })}
          </ul>
        </div>
      )}
    </div>
  )
}

function EmptyState() {
  return (
    <div className="relative overflow-hidden rounded-xl border border-border/60 bg-gradient-to-br from-white via-white to-emerald-50/30 px-6 py-10 shadow-sm">
      {/* Decorative hairline cross */}
      <div className="pointer-events-none absolute right-8 top-8 h-16 w-16 opacity-20" aria-hidden>
        <div className="absolute left-1/2 top-0 h-full w-px -translate-x-1/2 bg-foreground" />
        <div className="absolute top-1/2 left-0 h-px w-full -translate-y-1/2 bg-foreground" />
      </div>
      <div className="flex items-start gap-4">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-emerald-100/70 text-emerald-700">
          <Coffee className="h-5 w-5" />
        </div>
        <div className="flex-1">
          <p className="text-base font-medium tracking-tight text-foreground">
            <span className="italic">All quiet on the portfolio.</span> Pour another coffee.
          </p>
          <p className="mt-1.5 text-sm text-muted-foreground">
            Every property has synced recently, no rate drift, no failed pushes. We&apos;ll let you know
            the moment something needs you.
          </p>
        </div>
      </div>
    </div>
  )
}
